import React, { useEffect, useState, useCallback } from "react"; // React is used in JSX, so it should remain.
import { gql, useLazyQuery } from "@apollo/client";
import TimeTableInput from "./timeTableInput";
import { useMutation } from "@apollo/client";

const GET_STUDENT_WEEK = gql`
    query GetStudentWeek($studentId: String!, $weekNumber: Int!) {
        getStudentWeek(studentId: $studentId, weekNumber: $weekNumber) {
            weekNumber
            days {
                date
                weekNumber
                dayOfWeek
                dayName
                dayShortName
                holiday {
                    name
                }
                unitCells {
                    weekNumber
                    dayOfWeek
                    unitIndex
                    Editable
                    Content
                }
            }
            timeDescriptions {
                index
                startTime
                endTime
            }
            stats {
                exits
                studies
            }
        }
        schoolYearOverview {
            normalStudentUnits
        }
    }
`;

const SET_STUDENT_UNIT = gql`
    mutation SetStudentUnit($studentId: String!, $weekNumber: Int!, $dayNumber: Int!, $unitNumber: Int!, $content: String!) {
        setStudentUnit(studentId: $studentId, weekNumber: $weekNumber, dayNumber: $dayNumber, unitNumber: $unitNumber, content: $content)
    }
`;

export default function TeacherStudentWeekView({ weekIndex, studentId, refreshRef }) {
    const [fetchWeek, { data, error }] = useLazyQuery(GET_STUDENT_WEEK, { fetchPolicy: 'no-cache' });
    const [setStudentUnitMutation] = useMutation(SET_STUDENT_UNIT);
    
    // expose a ref callback to allow a parent to trigger a refresh of this view
    useEffect(() => {
        if (refreshRef) {
            // set the ref to a function that forces a network fetch
            refreshRef.current = () => {
                if (weekIndex != null && studentId) {
                    fetchWeek({ variables: { weekNumber: weekIndex, studentId }, fetchPolicy: 'network-only' });
                }
            };
            return () => {
                try { refreshRef.current = null; } catch (e) {}
            };
        }
    }, [refreshRef, fetchWeek, weekIndex, studentId]);

    // Local state for table data to prevent constant re-rendering
    const [localWeekData, setLocalWeekData] = useState(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [userInfo, setUserInfo] = useState(null);
    const [schoolYearInfo, setSchoolYearInfo] = useState(null);

    // Update local state when server data changes
    useEffect(() => {
        if (data && data.getStudentWeek) {
            setLocalWeekData(data.getStudentWeek);
            setUserInfo(data.me);
            setSchoolYearInfo(data.schoolYearOverview);
            setIsInitialLoading(false);
        }
    }, [data]);

    useEffect(() => {
        if (weekIndex != null && studentId) {
            setIsInitialLoading(true);
            fetchWeek({ variables: { weekNumber: weekIndex, studentId: studentId } });
        }
    }, [weekIndex, studentId, fetchWeek]);

    // Helper to get cell content from local data
    const getCellValue = useCallback((dayIdx, unitIdx) => {
        if (!localWeekData || !localWeekData.days) return "";
        const cell = localWeekData.days[dayIdx]?.unitCells.find(c => c.unitIndex === unitIdx);
        return cell?.Content || "";
    }, [localWeekData]);

    // Helper to update local cell data immediately
    const updateLocalCellData = useCallback((dayIndex, unitIndex, newContent) => {
        if (!localWeekData) return;
        
        setLocalWeekData(prevData => {
            const updatedData = JSON.parse(JSON.stringify(prevData)); // Deep clone
            const day = updatedData.days[dayIndex];
            if (day) {
                let cell = day.unitCells.find(c => c.unitIndex === unitIndex);
                if (cell) {
                    cell.Content = newContent;
                } else {
                    // Create new cell if it doesn't exist
                    day.unitCells.push({
                        weekNumber: updatedData.weekNumber,
                        dayOfWeek: day.dayOfWeek,
                        unitIndex: unitIndex,
                        Editable: true,
                        Content: newContent
                    });
                }
            }
            return updatedData;
        });
    }, [localWeekData]);

    // Callback for cell edit - updates local state immediately, then syncs with server
    const handleCellChange = async (oldValue, newValue, timeUnitIndex, dayIndex) => {
        if (!localWeekData) return;
        
        // Update local state immediately for responsive UI
        updateLocalCellData(dayIndex, timeUnitIndex, newValue);
        
        const day = localWeekData.days[dayIndex];
        const weekNumber = localWeekData.weekNumber;
        const dayNumber = day.dayOfWeek;
        const unitNumber = timeUnitIndex;
        
        try {
            await setStudentUnitMutation({
                variables: {
                    studentId,
                    weekNumber,
                    dayNumber,
                    unitNumber,
                    content: newValue
                }
            });

            // Silently refetch data in background to sync with server
            // This won't cause UI flickering since we're using local state
            fetchWeek({ 
                variables: { weekNumber, studentId },
                fetchPolicy: 'network-only' // Force fresh data from server
            });
        } catch (err) {
            console.error("Error saving:", err); // Updated text to English.
            // Revert local state on error
            updateLocalCellData(dayIndex, timeUnitIndex, oldValue);
        }
    };

    if (weekIndex == null) return <div>Please select a week.</div>;
    if (!studentId) return <div>Please select a student.</div>;
    if (isInitialLoading) return <div>Loading week data...</div>;
    if (error) return <div className="text-red-600">Error: {error.message}</div>;
    if (!localWeekData) return <div>No data for this week.</div>;

    const isBoarding = userInfo?.boarding;
    const normalStudentUnits = schoolYearInfo?.normalStudentUnits || 0;
    const days = localWeekData.days;
    const timeUnits = localWeekData.timeDescriptions;

    function formatDate(dateString) {
        const date = new Date(dateString);
        if (isNaN(date)) return "";
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = String(date.getFullYear()).slice(-2);
        return `${day}.${month}.${year}`;
    }

    return (
        <div className="w-full overflow-x-auto">
            <table
                className="min-w-full text-base overflow-x-auto"
                style={{ borderCollapse: 'separate', borderSpacing: '4px 0px' }}
            >
                <thead>
                    <tr>
                        <th className="py-1 max-w-[64px]"> </th>
                        {days.map(day => {
                            const isHoliday = day.holiday != null;
                            return (
                                <th
                                    className="py-1 w-[110px] font-bold text-center border-b border-gray-600"
                                    key={day.dayOfWeek}
                                >
                                    {isHoliday ? (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <span className="text-gray-500 italic">{day.holiday.name}</span>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="hidden md:inline">{day.dayName}</span>
                                            <span className="md:hidden">{day.dayShortName}</span>
                                            <p className="text-xs text-gray-500 italic">
                                                {formatDate(day.date)}
                                            </p>
                                        </>
                                    )}
                            </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {timeUnits
                        .slice(
                            0,
                            isBoarding
                                ? timeUnits.length
                                : normalStudentUnits
                        )
                        .map((unit, rowIdx) => {
                            const { startTime, endTime } = unit;
                            return (
                                <tr key={rowIdx} className="h-[64px]">
                                    <td className="py-1 w-[64px] text-sm md:text-md font-bold text-left bg-white align-middle">
                                        <span className="hidden md:inline">{startTime} - {endTime}</span>
                                        <span className="md:hidden">{startTime} <br />{endTime}</span>
                                    </td>
                                    {days.map((day, colIdx) => {
                                        const cell = day.unitCells.find(c => c.unitIndex === rowIdx);
                                        const isHoliday = day.holiday != null;
                                        const top = rowIdx === 0 ? null : getCellValue(colIdx, rowIdx-1);
                                        const bottom = rowIdx === timeUnits.length - 1 ? null : getCellValue(colIdx, rowIdx+1);
                                        return (
                                            <td className="relative min-w-[64px] h-full p-0" key={colIdx}>
                                                {isHoliday ? (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        
                                                    </div>
                                                ) : (
                                                    <TimeTableInput
                                                        data={getCellValue(colIdx, rowIdx)}
                                                        editable={cell?.Editable}
                                                        dataTop={top}
                                                        dataBottom={bottom}
                                                        timeUnitIndex={rowIdx}
                                                        dayIndex={colIdx}
                                                        onChange={handleCellChange}
                                                    />
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                </tbody>
            </table>
        </div>
    );
}
